import { RootState } from "src/store";
import { connect } from "react-redux";
// Stack spaces the four custom-filter Autocomplete fields
import { Autocomplete, AutocompleteProps, Box, createFilterOptions, FilterOptionsState, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useCallback, useMemo, useRef } from "react";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { filterActions } from "../../filter-store";
import { TCustomFilter } from "../../filter-types";

const mapStateToProps = (state: RootState) => ({
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData,
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState
});

const ownActions = {
  setFormDataCustomFilters: filterActions.setFormDataCustomFilters
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

// The configuration-name field carries one option per configuration in the project, and Autocomplete
// renders every match in the popup — on a large project that is hundreds of list items built on each
// open. Capping the rendered set keeps opening the dropdown cheap.
//
// The cap applies to the MATCHES, not to the options: MUI filters the full list first and slices
// afterwards, so typing still reaches every configuration. Only browsing from an empty input is
// bounded — and the paper footer below says so rather than truncating silently.
const CONFIGURATION_NAME_OPTIONS_LIMIT = 100;
const matchConfigurationName = createFilterOptions<TCustomFilter>({ stringify: option => option.description });

const CustomFilterBase = (props: Props) => {
  const { formData, configurationsState, setFormDataCustomFilters } = props;

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();
  const theme = useTheme();

  // Written while filtering, read while rendering the paper below it in the same pass — MUI calls
  // filterOptions from the Autocomplete's own render, and the paper is a child of that element.
  const configurationNameMatchCount = useRef({ shown: 0, total: 0 });

  const filterConfigurationNameOptions = useCallback((options: TCustomFilter[], state: FilterOptionsState<TCustomFilter>) => {
    const matches = matchConfigurationName(options, state);
    const shown = matches.slice(0, CONFIGURATION_NAME_OPTIONS_LIMIT);
    configurationNameMatchCount.current = { shown: shown.length, total: matches.length };
    return shown;
  }, []);

  // Sits under the scrolling option list, so the count stays visible however far down the user is
  const ConfigurationNamePaper: AutocompleteProps<TCustomFilter, true, false, false>["slots"] = useMemo(
    () => ({
      paper: paperProps => {
        const { children, ...rest } = paperProps;
        const { shown, total } = configurationNameMatchCount.current;
        return (
          <Paper {...rest}>
            {children}
            {total > shown && (
              <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${theme.palette.customColors.grey2}` }}>
                <Typography variant="caption" color="text.secondary">
                  {shown} / {total} — {configuratorPageT.keepTypingToNarrowTheList}
                </Typography>
              </Box>
            )}
          </Paper>
        );
      }
    }),
    [theme, configuratorPageT.keepTypingToNarrowTheList]
  );

  // only missingInfo items for this field's controlled value
  const selectedMissingInfoFilters = (formData.customFilters || []).filter(x => x.filterType === "missingInfo");
  // only invalidInfo items for this field's controlled value
  const selectedInvalidInfoFilters = (formData.customFilters || []).filter(x => x.filterType === "invalidInfo");
  // only productType items for the second field's controlled value
  const selectedProductTypeFilters = (formData.customFilters || []).filter(x => x.filterType === "productType");
  // only systemProductConfigurationName items for this field's controlled value
  const selectedConfigurationNameFilters = (formData.customFilters || []).filter(x => x.filterType === "configurationName");

  const missingInfoOptions = useMemo<TCustomFilter[]>(
    () => [
      { id: "yes", description: genericTranslationsT.yes, filterType: "missingInfo" },
      { id: "no", description: genericTranslationsT.no, filterType: "missingInfo" }
    ],
    [genericTranslationsT]
  );

  const invalidInfoOptions = useMemo<TCustomFilter[]>(
    () => [
      { id: "yes", description: genericTranslationsT.yes, filterType: "invalidInfo" },
      { id: "no", description: genericTranslationsT.no, filterType: "invalidInfo" }
    ],
    [genericTranslationsT]
  );

  // derive unique product types from all line items in loaded configurations; sorted alphabetically
  const productTypeOptions = useMemo<TCustomFilter[]>(() => {
    if (!configurationsState?.configurations) return [];
    const seen = new Set<string>();
    const options: TCustomFilter[] = [];
    configurationsState.configurations.forEach(config => {
      // systemProductConfigurationItems replaces projectLineItems on the list-view model
      (config.systemProductConfigurationItems || []).forEach(pli => {
        if (pli.productTypeId && !seen.has(pli.productTypeId)) {
          seen.add(pli.productTypeId);
          options.push({ id: pli.productTypeId, description: pli.productTypeName || pli.productTypeId, filterType: "productType" });
        }
      });
    });
    return options.sort((a, b) => a.description.localeCompare(b.description));
  }, [configurationsState]);

  // derive unique configuration names from loaded configurations; sorted alphabetically
  const configurationNameOptions = useMemo<TCustomFilter[]>(() => {
    if (!configurationsState?.configurations) return [];
    return (
      configurationsState.configurations
        // systemProductConfigurationId replaces configurationId on the list-view model
        .filter(
          (config): config is typeof config & { systemProductConfigurationId: string; systemProductConfigurationName: string } =>
            !!config.systemProductConfigurationId && !!config.systemProductConfigurationName
        )
        .map(config => ({
          id: config.systemProductConfigurationId,
          description: config.systemProductConfigurationName,
          filterType: "configurationName" as const
        }))
        .sort((a, b) => a.description.localeCompare(b.description))
    );
  }, [configurationsState]);

  return (
    // Stack spaces the four custom-filter Autocomplete fields
    <Stack gap={2}>
      <Autocomplete<TCustomFilter, true>
        value={selectedMissingInfoFilters}
        onChange={(_, values) => {
          // preserve productType and systemProductConfigurationName partitions when missingInfo selection changes
          const otherItems = (formData.customFilters || []).filter(x => x.filterType !== "missingInfo");
          setFormDataCustomFilters([...otherItems, ...values]);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionDisabled={option => selectedMissingInfoFilters.some(selected => selected.id === option.id)}
        size="small"
        multiple
        options={missingInfoOptions}
        getOptionLabel={option => option.description || ""}
        filterSelectedOptions
        renderInput={params => <TextField {...params} label={configuratorPageT.isMissingInformation} />}
      />
      {/* Autocomplete for filtering by invalid information */}
      <Autocomplete<TCustomFilter, true>
        value={selectedInvalidInfoFilters}
        onChange={(_, values) => {
          // preserve all other partitions when invalidInfo selection changes
          const otherItems = (formData.customFilters || []).filter(x => x.filterType !== "invalidInfo");
          setFormDataCustomFilters([...otherItems, ...values]);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionDisabled={option => selectedInvalidInfoFilters.some(selected => selected.id === option.id)}
        size="small"
        multiple
        options={invalidInfoOptions}
        getOptionLabel={option => option.description || ""}
        filterSelectedOptions
        renderInput={params => <TextField {...params} label={configuratorPageT.isInvalidInformation} />}
      />
      {/* second Autocomplete for filtering by product type */}
      <Autocomplete<TCustomFilter, true>
        value={selectedProductTypeFilters}
        onChange={(_, values) => {
          // preserve missingInfo and systemProductConfigurationName partitions when productType selection changes
          const otherItems = (formData.customFilters || []).filter(x => x.filterType !== "productType");
          setFormDataCustomFilters([...otherItems, ...values]);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        size="small"
        multiple
        options={productTypeOptions}
        getOptionLabel={option => option.description || ""}
        filterSelectedOptions
        renderInput={params => <TextField {...params} label={configuratorPageT.productType} />}
      />
      {/* third Autocomplete for filtering by configuration name */}
      <Autocomplete<TCustomFilter, true>
        value={selectedConfigurationNameFilters}
        onChange={(_, values) => {
          // preserve missingInfo and productType partitions when systemProductConfigurationName selection changes
          const otherItems = (formData.customFilters || []).filter(x => x.filterType !== "configurationName");
          setFormDataCustomFilters([...otherItems, ...values]);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        size="small"
        multiple
        options={configurationNameOptions}
        filterOptions={filterConfigurationNameOptions}
        slots={ConfigurationNamePaper}
        getOptionLabel={option => option.description || ""}
        filterSelectedOptions
        renderInput={params => <TextField {...params} label={configuratorPageT.configurationName} />}
      />
    </Stack>
  );
};

export const CustomFilter = connect(mapStateToProps, ownActions)(CustomFilterBase);
